import "./TemplateTaskPanelRight.css";

import { Dispatch, SetStateAction } from "react";

import { AsteriskButtonIcon, MilestoneFlagIcon, RepeatIcon } from "../../../../assets";
import { TemplateTaskConstants } from "../../../../constants";
import { ProjectPhase } from "../../../../constants/project-phase";
import {
  DeadlineCalendarUnit,
  DeadlineOperator,
  DefaultDeadline,
  SelectData,
  TemplateTaskPanelData,
} from "../../../../models";
import { camelCaseToSentenceCase } from "../../../../utils";
import { Button, Column, FormLabel, MultiSelect, ReadonlyText, Row, Select, TextInput } from "../../../../widget";
import { useTemplateTaskPanelRight } from "./useTemplateTaskPanelRight";

export interface TemplateTaskPanelRightProps {
  currentStepPhase: string | undefined;
  organisationTypeSelectData: SelectData;
  stepSelectData: SelectData;
  phaseKeyDatesSelectData: SelectData;

  currentTemplateTask: TemplateTaskPanelData;
  setCurrentTemplateTask: Dispatch<SetStateAction<TemplateTaskPanelData>>;

  defaultDeadline: DefaultDeadline;
  setDefaultDeadline: Dispatch<SetStateAction<DefaultDeadline>>;
  mustSelectedOrgTypeVisibility: string;
  shouldHideOrganisationFields: boolean;
}

export const TemplateTaskPanelRight = ({
  currentStepPhase,
  organisationTypeSelectData,
  stepSelectData,
  phaseKeyDatesSelectData,
  currentTemplateTask,
  setCurrentTemplateTask,

  defaultDeadline,
  setDefaultDeadline,
  mustSelectedOrgTypeVisibility,
  shouldHideOrganisationFields,
}: TemplateTaskPanelRightProps): JSX.Element => {
  const { availableOperator } = useTemplateTaskPanelRight({
    currentTemplateTask,
    currentStepPhase,
    setDefaultDeadline,
  });
  const isOngoingMonitoringPhase = currentStepPhase === ProjectPhase.ongoingMonitoring;
  return (
    <div className="TemplateTaskPanelRight">
      <div className="TemplateTaskPanelRightMainActions">
        <Row spacingV="ml">
          <Column span={12}>
            <FormLabel label="Phase" />
            <ReadonlyText
              value={currentStepPhase !== undefined ? camelCaseToSentenceCase(currentStepPhase) : currentStepPhase}
            />
          </Column>
        </Row>
        <Row spacingV="ml">
          <Column span={12}>
            <Select
              label="Step*"
              placeholder="Select step"
              data={stepSelectData}
              value={currentTemplateTask?.workflowTemplateStepUuid}
              onChange={(value) => setCurrentTemplateTask({ ...currentTemplateTask, workflowTemplateStepUuid: value })}
            />
          </Column>
        </Row>
        <Row spacingV="s">
          <Column span={12}>
            <FormLabel label="Deadline*" />
          </Column>
        </Row>
        <Row spacingV="m">
          <Column span={12}>
            <Select
              placeholder="Select deadline date"
              value={currentTemplateTask?.defaultDeadlineField}
              data={phaseKeyDatesSelectData ?? []}
              onChange={(value) => setCurrentTemplateTask({ ...currentTemplateTask, defaultDeadlineField: value })}
            />
          </Column>
        </Row>
        <Row spacingV="ml">
          <Column span={4}>
            <Select
              placeholder=""
              value={defaultDeadline.operator?.toString() ?? availableOperator[0]?.value?.toString()}
              data={availableOperator}
              onChange={(value) => setDefaultDeadline({ ...defaultDeadline, operator: value as DeadlineOperator })}
            />
          </Column>
          <Column span={4}>
            <TextInput
              placeholder=""
              name="Deadline value"
              type="number"
              value={defaultDeadline.value.toString()}
              onChange={(value) => setDefaultDeadline({ ...defaultDeadline, value: Number(value) })}
              minNumberValue={0}
              maxNumberValue={100}
            />
          </Column>
          <Column span={4}>
            <Select
              placeholder=""
              data={TemplateTaskConstants.DEADLINE_CALENDAR_UNIT_SELECT_DATA}
              value={defaultDeadline.unit}
              onChange={(value) => setDefaultDeadline({ ...defaultDeadline, unit: value as DeadlineCalendarUnit })}
            />
          </Column>
        </Row>
        {!shouldHideOrganisationFields && (
          <>
            <Row spacingV="ml">
              <Column span={12}>
                <Select
                  label="Owner organisation type*"
                  placeholder="Select type"
                  value={currentTemplateTask?.ownerOrganisationType}
                  data={organisationTypeSelectData}
                  onChange={(value) => setCurrentTemplateTask({ ...currentTemplateTask, ownerOrganisationType: value })}
                />
              </Column>
            </Row>
            <Row>
              <Column span={12}>
                <MultiSelect
                  label="Visibility*"
                  placeholder="Select organisation(s)"
                  values={currentTemplateTask?.defaultVisibility}
                  data={organisationTypeSelectData}
                  onChange={(value) => {
                    const newValue = value.includes(mustSelectedOrgTypeVisibility)
                      ? value
                      : [...value, mustSelectedOrgTypeVisibility];
                    setCurrentTemplateTask({ ...currentTemplateTask, defaultVisibility: newValue });
                  }}
                />
              </Column>
            </Row>
          </>
        )}
      </div>
      <div className="TemplateTaskPanelRightOtherActions">
        <Row spacingV="ml">
          <Column span={12}>
            <p className="body01">Other actions</p>
          </Column>
        </Row>
        <Row spacingV="xs">
          <Column span={12}>
            <div className="TemplateTaskPanelRightOtherActionsItem">
              <FormLabel
                label={
                  (currentTemplateTask.defaultIsMilestone ?? false)
                    ? "Remove as milestone task"
                    : "Set as milestone task"
                }
              />
              <Button
                text=""
                icon={
                  <MilestoneFlagIcon
                    className={`MilestoneFlagGreyIcon ${(currentTemplateTask.defaultIsMilestone ?? false) && "Active"}`}
                  />
                }
                variant="quaternary"
                onClick={() =>
                  setCurrentTemplateTask({
                    ...currentTemplateTask,
                    defaultIsMilestone: !(currentTemplateTask.defaultIsMilestone ?? false),
                  })
                }
                minWidth={false}
                hasNoPadding
              />
            </div>
          </Column>
        </Row>
        {/* NOTE - hiding this for now KAN-7325 - https://kanaearth.atlassian.net/browse/KAN-7325 */}
        {/* {currentTemplateTask.defaultIsMilestone && (
          <Row spacingV="m">
            <Column span={12}>
              <Select
                label="Update key project date"
                tooltip={
                  <KanaTooltip
                    tooltipHeader=""
                    tooltipText="Selecting a date here will update the chosen key project date with this task’s date"
                  />
                }
                placeholder="Select key date"
                value={currentTemplateTask?.defaultKeyDate}
                data={phaseKeyDatesSelectData}
                onChange={(value) => setCurrentTemplateTask({ ...currentTemplateTask, defaultKeyDate: value })}
              />
            </Column>
          </Row>
        )} */}
        <Row spacingV="xs">
          <Column span={12}>
            <div className="TemplateTaskPanelRightOtherActionsItem">
              <FormLabel
                label={
                  (currentTemplateTask.defaultIsMandatory ?? false)
                    ? "Remove mandatory status"
                    : "Set as mandatory task"
                }
              />
              <Button
                text=""
                icon={
                  <AsteriskButtonIcon
                    className={`AsteriskButtonIcon ${(currentTemplateTask.defaultIsMandatory ?? false) && "Active"}`}
                  />
                }
                variant="quaternary"
                onClick={() =>
                  setCurrentTemplateTask({
                    ...currentTemplateTask,
                    defaultIsMandatory: !(currentTemplateTask.defaultIsMandatory ?? false),
                  })
                }
                minWidth={false}
                hasNoPadding
              />
            </div>
          </Column>
        </Row>
        {isOngoingMonitoringPhase && (
          <Row spacingV="xs">
            <Column span={12}>
              <div className="TemplateTaskPanelRightOtherActionsItem">
                <FormLabel
                  label={
                    (currentTemplateTask.defaultRepeat ?? false)
                      ? "Remove as repeatable task"
                      : "Set as repeatable task"
                  }
                />
                <Button
                  text=""
                  icon={
                    <RepeatIcon className={`RepeatIcon ${(currentTemplateTask.defaultRepeat ?? false) && "Active"}`} />
                  }
                  variant="quaternary"
                  onClick={() =>
                    setCurrentTemplateTask({
                      ...currentTemplateTask,
                      defaultRepeat: !(currentTemplateTask.defaultRepeat ?? false),
                    })
                  }
                  minWidth={false}
                  hasNoPadding
                />
              </div>
            </Column>
          </Row>
        )}
      </div>
    </div>
  );
};
